import React from "react";
import { Reveal, PageTransition } from "@/components/Reveal";
import { Seo } from "@/components/Seo";

const LegalLayout = ({ title, updated, sections, testid, seo }) => (
  <PageTransition>
    <Seo {...seo} />
    <section className="border-b border-slate-100 bg-slate-50">
      <div className="mx-auto max-w-4xl px-5 py-20 sm:px-8">
        <Reveal>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">{title}</h1>
          <p className="mt-4 text-sm text-slate-500">Last updated: {updated}</p>
        </Reveal>
      </div>
    </section>
    <section className="mx-auto max-w-4xl px-5 py-16 sm:px-8" data-testid={testid}>
      <div className="space-y-10">
        {sections.map((s, i) => (
          <Reveal key={s.heading} delay={i * 0.04}>
            <div>
              <h2 className="text-xl font-semibold text-slate-900">{s.heading}</h2>
              <p className="mt-3 leading-relaxed text-slate-600">{s.body}</p>
            </div>
          </Reveal>
        ))}
        <p className="border-t border-slate-100 pt-8 text-sm text-slate-400">
          This document is provided for general informational purposes and does not constitute legal
          advice. For questions, contact us at legal@hewto.ai.
        </p>
      </div>
    </section>
  </PageTransition>
);

export const PrivacyPolicy = () => (
  <LegalLayout
    title="Privacy Policy"
    updated="May 2025"
    testid="privacy-policy-page"
    seo={{
      title: "Privacy Policy | Hewto.ai",
      description: "How Hewto.ai collects, uses, and protects information — including protected health information (PHI) — across our intelligent document processing platform for US healthcare.",
      canonical: "https://www.hewto.ai/privacy-policy",
    }}
    sections={[
      { heading: "Overview", body: "Hewto.ai (\u201cwe\u201d, \u201cus\u201d) is committed to protecting the privacy and security of the information we process. This policy explains what we collect, how we use it, and the choices you have." },
      { heading: "Information we collect", body: "We collect account and contact details you provide, along with documents and data submitted for processing. We also collect limited technical information such as log data used to operate and secure the service." },
      { heading: "Protected health information", body: "When we process protected health information (PHI) on behalf of a customer, we handle it in accordance with our data processing agreement and applicable data-protection obligations, using appropriate safeguards." },
      { heading: "How we use information", body: "We use information to provide document extraction and validation, to operate and improve the service, to maintain security and audit trails, and to communicate with you about your account." },
      { heading: "Security", body: "We apply administrative, technical, and physical safeguards including encryption in transit and at rest, access controls, and continuous monitoring." },
      { heading: "Data retention", body: "We retain data only as long as necessary to provide the service and meet legal, contractual, and compliance obligations, after which it is securely deleted." },
      { heading: "Your rights", body: "Depending on your jurisdiction, you may have rights to access, correct, or delete personal information. Contact privacy@hewto.ai to exercise these rights." },
    ]}
  />
);

export const Terms = () => (
  <LegalLayout
    title="Terms & Conditions"
    updated="May 2025"
    testid="tnc-page"
    seo={{
      title: "Terms & Conditions | Hewto.ai",
      description: "The terms governing use of the Hewto.ai intelligent document processing platform for US healthcare — accounts, service levels, intellectual property, and liability.",
      canonical: "https://www.hewto.ai/tnc",
    }}
    sections={[
      { heading: "Acceptance of terms", body: "By accessing or using Hewto.ai, you agree to be bound by these Terms & Conditions and any applicable order form or service agreement." },
      { heading: "Use of the service", body: "You may use the service only in compliance with these terms and all applicable laws, including healthcare and data-protection regulations. You are responsible for the accuracy and lawfulness of data you submit." },
      { heading: "Accounts & access", body: "You are responsible for maintaining the confidentiality of your account credentials and for all activity that occurs under your account." },
      { heading: "Service levels", body: "Availability, support, and performance commitments are described in your applicable order form or service-level agreement. We continually work to improve reliability and accuracy." },
      { heading: "Intellectual property", body: "Hewto.ai and its underlying technology are owned by us. You retain all rights to the documents and data you submit for processing." },
      { heading: "Limitation of liability", body: "To the maximum extent permitted by law, our aggregate liability is limited as set out in your service agreement. The service is provided on an \u201cas is\u201d basis except as expressly stated." },
      { heading: "Changes", body: "We may update these terms from time to time. Continued use of the service after changes constitutes acceptance of the revised terms." },
    ]}
  />
);

export const Refund = () => (
  <LegalLayout
    title="Refund Policy"
    updated="May 2025"
    testid="refund-page"
    seo={{
      title: "Refund Policy | Hewto.ai",
      description: "Hewto.ai refund and cancellation terms — fees, prepaid amounts, usage charges, and enterprise-agreement handling for our healthcare document processing platform.",
      canonical: "https://www.hewto.ai/refund",
    }}
    sections={[
      { heading: "Fees", body: "Fees for Hewto.ai are set out in your order form or agreement and billed as specified therein. Fees are non-refundable except where required by law or expressly stated in your order form." },
      { heading: "Cancellations", body: "You may cancel in accordance with the terms of your agreement. Cancellation takes effect at the end of your current term, and you will retain access until then." },
      { heading: "Prepaid terms", body: "Prepaid amounts are not refundable on a pro-rata basis unless otherwise agreed in writing in your agreement." },
      { heading: "Usage charges", body: "Charges for usage above the volumes agreed in your order form are billed in arrears and are non-refundable once processing has been performed." },
      { heading: "Enterprise agreements", body: "Refund and credit terms for enterprise customers are governed by the specific terms of the applicable master services agreement." },
      { heading: "How to request", body: "For billing questions or refund requests permitted under this policy, contact billing@hewto.ai and our team will respond within one business day." },
    ]}
  />
);
